import { useState } from "react";
import { useDispatch } from "react-redux";
import FileSaver from "file-saver";
import JSZip from "jszip";
import { toast } from "react-toastify";

const useDocumentDownloader = () => {
  const dispatch = useDispatch();
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadDocuments = async (folderIds, options = {}) => {
    const {
      getDocUrlAction,
      singleFileName = "folder.pdf",
      zipFileName = "folders.zip",
      loadingMessage = "Preparing documents for download...",
      downloadingMessage = "Downloading documents...",
      successMessage = "Documents downloaded successfully!",
      errorMessage = "Error downloading documents",
      emptySelectionMessage = "Please select folders to download.",
      noDocumentsMessage = "No documents found to download.",
    } = options;

    // Validate inputs
    if (!Array.isArray(folderIds) || folderIds.length === 0) {
      toast.error(emptySelectionMessage);
      return false;
    }

    if (!getDocUrlAction) {
      toast.error("Download action not provided");
      return false;
    }

    setIsDownloading(true);
    const loadingToastId = toast.loading(loadingMessage);

    try {
      // Collect all URLs from all API calls
      const allUrls = [];

      // Create promises for all API calls
      const apiPromises = folderIds.map((folderId) => {
        return new Promise((resolve, reject) => {
          const handleSuccess = (data) => {
            if (Array.isArray(data) && data.length > 0) {
              allUrls.push(...data);
            } else if (data && typeof data === "string") {
              allUrls.push(data);
            }
            resolve();
          };

          const handleError = (error) => {
            console.error(`Error fetching URLs for folder ${folderId}:`, error);
            reject(error);
          };

          dispatch(
            getDocUrlAction({
              folder_id: folderId,
              handleSuccess,
              onError: handleError,
            })
          );
        });
      });

      // Wait for all API calls to complete
      await Promise.allSettled(apiPromises);

      if (allUrls.length === 0) {
        toast.update(loadingToastId, {
          render: noDocumentsMessage,
          type: "warning",
          isLoading: false,
          autoClose: 3000,
        });
        return false;
      }

      // Update loading message
      toast.update(loadingToastId, {
        render: `${downloadingMessage} (${allUrls.length} document${
          allUrls.length > 1 ? "s" : ""
        })`,
        isLoading: true,
      });

      const success = await processDownload(
        allUrls,
        singleFileName,
        zipFileName
      );

      if (success.downloaded > 0) {
        const message =
          success.total === success.downloaded
            ? `${successMessage} (${success.downloaded} document${
                success.downloaded > 1 ? "s" : ""
              })`
            : `${success.downloaded} of ${success.total} documents downloaded successfully.`;

        toast.update(loadingToastId, {
          render: message,
          type: "success",
          isLoading: false,
          autoClose: 3000,
        });
        return true;
      } else {
        toast.update(loadingToastId, {
          render: "Failed to download any documents.",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
        return false;
      }
    } catch (error) {
      console.error("Error in download process:", error);
      toast.update(loadingToastId, {
        render: errorMessage,
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
      return false;
    } finally {
      setIsDownloading(false);
    }
  };

  const processDownload = async (urls, singleFileName, zipFileName) => {
    if (urls.length === 1) {
      // Download single PDF directly
      try {
        const response = await fetch(urls[0]);
        if (!response.ok) throw new Error("Failed to fetch document");

        const blob = await response.blob();
        FileSaver.saveAs(blob, singleFileName);
        return { downloaded: 1, total: 1 };
      } catch (error) {
        console.error("Error downloading file:", error);
        return { downloaded: 0, total: 1 };
      }
    } else {
      // Create ZIP for multiple PDFs
      const zip = new JSZip();
      let successCount = 0;

      await Promise.allSettled(
        urls.map(async (url, index) => {
          try {
            const response = await fetch(url);
            if (!response.ok) {
              throw new Error(`Failed to fetch document from ${url}`);
            }

            const blob = await response.blob();
            zip.file(`document-${index + 1}.pdf`, blob);
            successCount++;
          } catch (error) {
            console.error(`Error fetching file ${url}:`, error);
          }
        })
      );

      if (successCount === 0) {
        return { downloaded: 0, total: urls.length };
      }

      try {
        const content = await zip.generateAsync({ type: "blob" });
        FileSaver.saveAs(content, zipFileName);
        return { downloaded: successCount, total: urls.length };
      } catch (error) {
        console.error("Error generating ZIP:", error);
        return { downloaded: 0, total: urls.length };
      }
    }
  };

  return {
    downloadDocuments,
    isDownloading,
  };
};

export default useDocumentDownloader;
